import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  type CdcDarkVesselsResponse,
  checkCdcHealth,
  fetchCdcDarkVessels,
  fetchCdcRelatedVessels,
  fetchCdcVessel,
  fetchCdcVesselHistory,
  fetchCdcVesselOwner,
  fetchCdcVesselsBbox,
  fetchCdcVesselsBboxSlim,
  fetchCdcVesselsClustered,
  fetchCdcVesselsGlobalSlim,
  searchCdcVessels,
} from '../api/cdcClient';
import type { RelatedOrderBy } from '../stores/useCdcStore';
import type {
  CdcBboxParams,
  CdcHealthResponse,
  CdcOwnerInfo,
  CdcRelatedVesselsResponse,
  CdcSearchResponse,
  CdcVessel,
  CdcVesselHistory,
} from '../types';

export interface RelatedVesselsFilter {
  nearby?: boolean; // If true, uses radius_km for nearby search
  same_type?: boolean;
  same_destination?: boolean;
  same_flag?: boolean;
  same_owner?: boolean;
  radius_km?: number; // Distance in km (converted to meters for API)
  limit?: number;
}

/**
 * Hook to check CDC API health status
 * Polls every 30 seconds
 */
export function useCdcHealth() {
  return useQuery<CdcHealthResponse, Error>({
    queryKey: ['cdc', 'health'],
    queryFn: checkCdcHealth,
    refetchInterval: 30000,
    retry: 1,
    staleTime: 10000,
  });
}

/**
 * Hook to fetch ALL vessels globally for client-side clustering (SLIM data)
 * Cassandra-style approach: Load once at startup, Mapbox GPU clusters instantly
 *
 * Trade-off: ~30s startup load, but INSTANT zoom/pan after that
 * Uses slim endpoint (5 fields only) for ~80% smaller payload
 *
 * Auto-refreshes every 5 minutes for fresh positions
 */
export function useCdcVesselsGlobal(enabled = true) {
  return useQuery<GeoJSON.FeatureCollection, Error>({
    queryKey: ['cdc', 'vessels', 'global', 'slim'],
    queryFn: () => fetchCdcVesselsGlobalSlim(600000), // Load ALL vessels globally (~554k)
    enabled,
    staleTime: 300000, // 5 minutes before considered stale
    refetchInterval: 300000, // Auto-refresh every 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch on tab switch
  });
}

/**
 * Hook to fetch CDC vessels within a bounding box (slim data for clustering)
 * Uses the slim endpoint which returns only: mmsi, lat, lon, ship_type, cog
 * ~80% smaller payload for faster map rendering
 */
export function useCdcVessels(bbox: CdcBboxParams | null) {
  return useQuery<GeoJSON.FeatureCollection, Error>({
    queryKey: ['cdc', 'vessels', 'slim', bbox],
    queryFn: () => {
      if (!bbox) {
        return Promise.resolve({
          type: 'FeatureCollection' as const,
          features: [],
        });
      }
      return fetchCdcVesselsBboxSlim(bbox);
    },
    enabled: !!bbox,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to fetch CDC vessels within a bounding box (full data)
 * Use this when you need all 14 fields per vessel
 */
export function useCdcVesselsFull(bbox: CdcBboxParams | null) {
  return useQuery<GeoJSON.FeatureCollection, Error>({
    queryKey: ['cdc', 'vessels', 'full', bbox],
    queryFn: () => {
      if (!bbox) {
        return Promise.resolve({
          type: 'FeatureCollection' as const,
          features: [],
        });
      }
      return fetchCdcVesselsBbox(bbox);
    },
    enabled: !!bbox,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to fetch a single CDC vessel by MMSI
 */
export function useCdcVessel(mmsi: string | null) {
  return useQuery<{ found: boolean; vessel?: CdcVessel }, Error>({
    queryKey: ['cdc', 'vessel', mmsi],
    queryFn: () => {
      if (!mmsi) {
        return Promise.resolve({ found: false });
      }
      return fetchCdcVessel(mmsi);
    },
    enabled: !!mmsi,
    staleTime: 60000,
  });
}

/**
 * Hook to fetch CDC vessel history for animation
 */
export function useCdcVesselHistory(mmsi: string | null, hours = 6, enabled = true) {
  // Backend caps at 10000 points; use max for longer ranges to avoid truncation
  const limit = hours > 24 ? 10000 : 1000;
  return useQuery<CdcVesselHistory, Error>({
    queryKey: ['cdc', 'vessel', 'history', mmsi, hours, limit],
    queryFn: () => {
      if (!mmsi) {
        throw new Error('MMSI is required');
      }
      return fetchCdcVesselHistory(mmsi, hours);
    },
    enabled: !!mmsi && enabled,
    staleTime: 60000,
  });
}

/**
 * Hook to fetch related CDC vessels (nearby, same type, etc.)
 * Supports combining multiple filters (AND logic)
 *
 * API uses radius_m (meters), so we convert radius_km to radius_m
 */
export function useCdcRelatedVessels(
  mmsi: string | null,
  filter: RelatedVesselsFilter | null,
  orderBy: RelatedOrderBy = 'recency',
  limit = 100,
) {
  const hasFilter =
    filter && (filter.nearby || filter.same_type || filter.same_destination || filter.same_flag || filter.same_owner);

  return useQuery<CdcRelatedVesselsResponse, Error>({
    queryKey: ['cdc', 'vessel', 'related', mmsi, filter, orderBy, limit],
    queryFn: () => {
      if (!mmsi || !filter) {
        return Promise.resolve({
          type: 'FeatureCollection' as const,
          features: [],
          total_count: 0,
          showing: 0,
          limit: 100,
        });
      }

      // Convert radius_km to radius_m for the API
      // If nearby is true, include radius_m parameter
      const radius_m = filter.nearby && filter.radius_km ? filter.radius_km * 1000 : undefined;

      return fetchCdcRelatedVessels(mmsi, {
        radius_m,
        same_type: filter.same_type,
        same_destination: filter.same_destination,
        same_flag: filter.same_flag,
        same_owner: filter.same_owner,
        order_by: orderBy,
        limit,
      });
    },
    enabled: !!mmsi && !!hasFilter,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch CDC vessel owner information
 */
export function useCdcVesselOwner(mmsi: string | null) {
  return useQuery<CdcOwnerInfo, Error>({
    queryKey: ['cdc', 'vessel', 'owner', mmsi],
    queryFn: () => {
      if (!mmsi) {
        return Promise.resolve({ found: false, mmsi: '' });
      }
      return fetchCdcVesselOwner(mmsi);
    },
    enabled: !!mmsi,
    staleTime: 300000, // 5 minutes - owner info doesn't change often
  });
}

/**
 * Hook to search CDC vessels by name, MMSI, or IMO
 * Only fetches when query has at least 2 characters
 */
export function useCdcSearch(query: string, limit = 10) {
  const trimmedQuery = query.trim();

  return useQuery<CdcSearchResponse, Error>({
    queryKey: ['cdc', 'search', trimmedQuery, limit],
    queryFn: () => searchCdcVessels(trimmedQuery, limit),
    enabled: trimmedQuery.length >= 2,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch CDC vessels with server-side clustering
 * Provides global coverage by fetching world-wide and letting backend cluster
 *
 * At low zoom (0-7): Returns pre-aggregated clusters with point_count (world-wide)
 * At high zoom (8+): Returns individual vessels (viewport bbox only)
 */
export function useCdcVesselsClustered(
  zoom: number | null,
  bbox?: { minLat: number; maxLat: number; minLon: number; maxLon: number } | null,
  enabled = true,
) {
  // At high zoom (8+), we need bbox to get relevant vessels
  // At low zoom (0-7), we use world-wide for global cluster coverage
  const useViewportBbox = zoom !== null && zoom >= 8;

  return useQuery<GeoJSON.FeatureCollection, Error>({
    queryKey: ['cdc', 'vessels', 'clustered', zoom, useViewportBbox ? bbox : null],
    queryFn: () => {
      if (zoom === null) {
        return Promise.resolve({
          type: 'FeatureCollection' as const,
          features: [],
        });
      }
      // At high zoom, use viewport bbox; at low zoom, fetch globally
      const bboxToUse = useViewportBbox && bbox ? bbox : undefined;
      return fetchCdcVesselsClustered(zoom, bboxToUse);
    },
    enabled: enabled && zoom !== null && (!useViewportBbox || bbox !== null),
    staleTime: 60000, // 60 seconds - don't refetch too often
    refetchOnWindowFocus: false,
    // Keep showing previous vessels while new data loads during zoom/pan
    // Prevents empty flash and makes interactions feel smooth
    placeholderData: keepPreviousData,
  });
}

/**
 * Hook to fetch dark vessels (vessels that haven't reported in X hours)
 * Returns GeoJSON with metadata including total count
 */
export function useCdcDarkVessels(hours = 72, limit = 1000, enabled = true) {
  return useQuery<CdcDarkVesselsResponse, Error>({
    queryKey: ['cdc', 'vessels', 'dark', hours, limit],
    queryFn: () => fetchCdcDarkVessels(hours, limit),
    enabled,
    staleTime: 300000, // 5 minutes - dark vessels don't change often
    refetchOnWindowFocus: false,
  });
}
